'use strict';
//***********************************************************************************
//***********************************************************************************
import { cn_add, cn_cart, cn_clone, cn_mul, cn_polar, cn_size, cn_sub, cnx_clone } from '../utils/cn_utilities';
import { cn_handler_object } from './cn_handler_object';
import { cn_camera } from './cn_camera';
import { cn_scene } from '../model/cn_scene';
import { cn_storey } from '../model/cn_storey';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_event_handler } from './cn_event_handler';
import { cn_wall } from '../model/cn_wall';
import { logger } from '../utils/cn_logger';
import { cn_space_segment } from '../model/cn_space_segment';
import { cn_pastille } from './cn_pastille';

/**
 * A handler to drag an object along the walls
 * @class cn_handler_wall_drag
 */

export class cn_handler_wall_drag extends cn_event_handler {

    //*****************************************************************
    /**
     * Constructor
     * @param {cn_storey} storey
     * @param {cn_wall} wall
     * @param {number} wall_side
     * @param {number} wall_position
     */
    constructor(storey, wall = null, wall_side = 0, wall_position = 0, wall_height = 0) {
        super();
        this.scene = storey.scene;
        this.storey = storey;

        this._space = null;
        this._best_position = null;

        this.radius = 10;
        this.creation_mode = (wall == null);
        this.altitude = 0;

        this._pastille = new cn_pastille([0, 0], 'arrow_all_white.svg');

        this.wall = wall;
        this.wall_side = wall_side;
        this.wall_position = wall_position;
        this.wall_height = wall_height;
        this.wall_impact = [0, 0, 0];

        this.set_wall(wall, wall_side, wall_position, wall_height);
    }

    set_wall(wall, wall_side, wall_position, wall_height = 0) {
        this.wall = wall;
        this.wall_side = wall_side;
        this.wall_position = wall_position;
        this.wall_height = wall_height;
        this.wall_impact = (wall) ? wall.compute_side_position_from_abcissa(wall_position, wall_side) : [0, 0];
        this.wall_impact = cnx_clone(this.wall_impact);
        this.wall_impact[2] = wall_height + this.storey.altitude;
        if (wall) {
            let ss = wall.find_space_segment(wall_side, wall_position);
            if (ss) this.wall_impact[2] += ss.space.slab_offset;
        }
    }
    //*****************************************************************
    /**
     * Draws the handler specific elements
     * @param {cn_camera} camera
     * @returns {string} returns a html string
     */
    draw(camera) {
        if (this.creation_mode) return "";

        var html = '';

        if (this.wall && !this.locked) {
            this._pastille.position = this.wall_impact;
            html += this._pastille.draw(camera);
            /*let p = camera.world_to_screen(this.wall_impact);
            if (p)
                html += `<circle fill="blue" cx="${p[0]}" cy="${p[1]}" r="5" />`;*/
        }
        return html;
    }

    //*****************************************************************
    /**
     * Clears move data
     */
    clear_move() {
        this._pastille.mouseover = false;
    }

    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    move(mouse_event) {
        if (this.creation_mode) {
            return this.check_place(mouse_event);
        }

        this._pastille.mouseover = !this.locked && this._pastille.contains_screen(mouse_event.mouse_screen, mouse_event.camera);
        return this._pastille.mouseover;
    }

    /**
     * Manage a grab. To return 'true' if grab is to be managed.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    grab(mouse_event) {
        if (this.creation_mode) return true;

        this.move(mouse_event);
        if (!this._pastille.mouseover) return false;
        return true;
    }

    /**
     * Manage a drag. Only after a grab that returned true. To return 'true' if drag had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns  {boolean}
     */
    drag(mouse_event) {
        if (this.creation_mode) return true;
        return this.check_place(mouse_event);
    }

    /**
     * During a drag and drop, the owner of the drag and drop may change.
     * Return true to accept owning the drag and drop events.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    grab_element(ev) {
        return true;
    }

    //*****************************************************************
    /**
     * Check a given place in the scene
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean} true if the event was caught
     */
    check_place(mouse_event) {
        this._best_position = null;

        //*** Special case for 3D */
        if (mouse_event.camera.is_3d()) {
            if (!mouse_event.impact_behind ||
                !mouse_event.impact_behind.storey_element ||
                mouse_event.impact_behind.storey_element.storey != this.storey ||
                mouse_event.impact_behind.storey_element.element.constructor != cn_space_segment ||
                Math.abs(mouse_event.impact_behind.normal[2]) > 0.1)
                return false;
            /** @type {cn_space_segment} */
            let space_segment = mouse_event.impact_behind.storey_element.element;
            this.wall = space_segment.wall;
            this.wall_side = space_segment.wall_side;
            this.wall_position = this.wall.compute_abcissa(mouse_event.impact_behind.position);
            this.wall_impact = cnx_clone(mouse_event.impact_behind.position);
            this.wall_height = mouse_event.impact_behind.position[2] - (this.storey.altitude + space_segment.space.slab_offset);
            this.call("change");
            return true;
        }

        var best_distance = 100;
        var best_impact = null;
        for (var theta = 0; theta < 360; theta += 15) {
            var direction = cn_cart([1, theta * Math.PI / 180]);
            var res = this.scene.raytrace_support_shape(mouse_event.mouse_world, direction, best_distance);
            if (!res || res.side >= 2) continue;
            best_distance = res.distance;
            best_impact = res;
        }
        if (best_impact == null) return false;

        this.wall = best_impact.wall;
        this.wall_side = best_impact.side;
        this.wall_position = best_impact.abcissa;
        this.wall_impact = cnx_clone(this.wall.compute_side_position_from_abcissa(best_impact.abcissa, this.wall_side));
        this.call("change");
        return true;
    }

    /**
     * Manage a drop. Only after a grab that returned true, and at least one drag. To return 'true' if drop had an effect.
     * @param {cn_mouse_event} mouse_event
     * @returns {boolean}
     */
    drop(mouse_event) {
        this._grabbed = false;
        return true;
    }
}

